import { SocialMediaType } from '__generated__/atlas-types'

import {
  isValidStoredSocialMediaForDisplay,
  resolveFacebookPageUrl,
} from './RetailerProfileEditMode/RetailerProfileEditMode.utils'

/**
 * Shared utilities for displaying retailer (and brand) social media and website links
 * so icons and URLs are consistent across RetailerProfile, Find New Retailers, and Submissions.
 */

export const SOCIAL_MEDIA_ICON_MAP = {
  FACEBOOK: 'facebook',
  INSTAGRAM: 'instagram',
  TWITTER: 'x',
} as const

export type SocialMediaIconName =
  (typeof SOCIAL_MEDIA_ICON_MAP)[keyof typeof SOCIAL_MEDIA_ICON_MAP]

export function getSocialMediaIcon(
  type?: SocialMediaType | null,
): SocialMediaIconName {
  switch (type) {
    case SocialMediaType.INSTAGRAM:
      return 'instagram'
    case SocialMediaType.TWITTER:
      return 'x'
    default:
      return 'facebook'
  }
}

/** Ensure URL has a protocol (adds https if missing). Used for website links. */
export function ensureHttps(url: string): string {
  if (!url) return url
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  return `https://${url}`
}

/** Force https for Facebook URLs. */
export function ensureHttpsForFacebook(url: string): string {
  if (!url) return url
  if (url.startsWith('https://')) return url
  if (url.startsWith('http://')) return url.replace('http://', 'https://')
  return `https://${url}`
}

/**
 * Build the href for a social media link from type and stored url (username, @username, or full URL).
 * Returns null if the url is invalid or should not be shown.
 */
export function getSocialMediaHref(
  type?: SocialMediaType | null,
  url?: string | null,
): string | null {
  if (!url?.trim()) {
    return null
  }
  if (!isValidStoredSocialMediaForDisplay(type, url)) {
    return null
  }

  // One resolver for both halves: whatever validation accepted, this returns,
  // so the editor and the rendered link can never disagree.
  if (type === SocialMediaType.FACEBOOK) {
    return resolveFacebookPageUrl(url)
  }

  if (type === SocialMediaType.INSTAGRAM || type === SocialMediaType.TWITTER) {
    if (
      url.includes('instagram.com/') ||
      url.includes('twitter.com/') ||
      url.includes('x.com/')
    ) {
      const normalized =
        type === SocialMediaType.TWITTER && url.includes('twitter.com')
          ? url.replace(/twitter\.com/gi, 'x.com')
          : url
      const withHttps = normalized.startsWith('http')
        ? normalized
        : `https://${normalized}`
      return type === SocialMediaType.TWITTER
        ? ensureHttpsForFacebook(withHttps.replace(/^http:\/\//i, 'https://'))
        : withHttps.startsWith('http://')
          ? withHttps.replace(/^http:\/\//i, 'https://')
          : withHttps
    }
    const username = url
      .replace('@', '')
      .trim()
      .replace(/^\/+|\/+$/g, '')
    if (!username) return null
    if (type === SocialMediaType.INSTAGRAM) {
      return `https://instagram.com/${username}`
    }
    return `https://x.com/${username}`
  }

  return null
}
